import { useLocalStorage } from '@uidotdev/usehooks';
import { Slot as SlotPrimitive } from 'radix-ui';
import * as React from 'react';
import { NavLink, useMatch } from 'react-router-dom';
import { useMedia } from 'react-use';

import { Button } from './Button';
import { Icon } from './Icon';
import { Input } from './Input';
import { EluveNameLogo } from './Logos';
import { Separator } from './Separator';
import { Sheet, SheetContent } from './Sheet';
import { Skeleton } from './Skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { VariantProps, cn, tv } from './utils/cn';

const Slot = SlotPrimitive.Slot;

const SIDEBAR_LOCAL_STORAGE_KEY = 'sidebar-open-state';
const SIDEBAR_WIDTH = '15rem';
const SIDEBAR_WIDTH_MOBILE = '15rem';
const SIDEBAR_WIDTH_ICON = '3.375rem';

type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  overlayOpen: boolean;
  setOverlayOpen: (open: boolean) => void;
  isMobile: boolean;
  /** True when the sidebar cannot be pinned at this viewport and presents as a sheet overlay instead. */
  isOverlay: boolean;
  toggleSidebar: () => void;
  /** True while the pointer is inside the desktop Sidebar. Drives the hover-revealed header trigger. */
  hovered: boolean;
  setHovered: (hovered: boolean) => void;
  /** True when the viewport is at least 80rem (1280px); below it the sidebar is locked collapsed and `setOpen` is a no-op. */
  canBeExpanded: boolean;
  /** Set by {@link Sidebar} on mount so the provider knows the collapse mode. */
  setCollapsible: (collapsible: 'offcanvas' | 'icon' | 'none') => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

/**
 * Reads sidebar state and actions from the nearest {@link SidebarProvider}.
 *
 * @example
 * const { state, toggleSidebar } = useSidebar();
 */
function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

/**
 * Provides sidebar state to its descendants and renders the wrapper element
 * holding the `--sidebar-width*` CSS custom properties.
 *
 * Manages three independent dimensions of state: pinned open/closed (persisted
 * to localStorage), overlay sheet open/closed, and a pointer-hover flag.
 * Wraps the entire app shell so {@link Sidebar} and content siblings can
 * coordinate via {@link useSidebar}.
 *
 * @example
 * <SidebarProvider>
 *   <Sidebar>…</Sidebar>
 *   <main>…</main>
 * </SidebarProvider>
 */
function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [collapsible, setCollapsible] = React.useState<
    'offcanvas' | 'icon' | 'none'
  >('icon');
  const isXL = useMedia('(min-width: 80rem)');
  const isMD = useMedia('(min-width: 48rem)');
  const canBeExpanded = isXL;
  const isMobile = !isMD;
  // Offcanvas has no rail: anywhere it can't be pinned, present as a sheet.
  const isOverlay = collapsible === 'offcanvas' ? !isXL : !isMD;

  const [overlayOpen, setOverlayOpen] = React.useState(false);
  const [storedOpen, setStoredOpen] = useLocalStorage(
    SIDEBAR_LOCAL_STORAGE_KEY,
    defaultOpen,
  );

  // Internal open state. `openProp` lets a parent take control if it wants.
  const [_open, _setOpen] = React.useState(storedOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      if (!canBeExpanded) {
        return;
      }

      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      setStoredOpen(openState);
    },
    [canBeExpanded, open, setOpenProp, setStoredOpen],
  );

  const toggleSidebar = React.useCallback(() => {
    return isOverlay
      ? setOverlayOpen((overlayOpen) => !overlayOpen)
      : setOpen((open) => !open);
  }, [isOverlay, setOpen, setOverlayOpen]);

  const [hovered, setHovered] = React.useState(false);

  // `state` exposes "expanded" / "collapsed" as a string so consumers can
  // gate behaviour and CSS via a `data-state` attribute.
  const state = canBeExpanded && open ? 'expanded' : 'collapsed';

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      isOverlay,
      overlayOpen,
      setOverlayOpen,
      toggleSidebar,
      hovered,
      setHovered,
      canBeExpanded,
      setCollapsible,
    }),
    [
      state,
      open,
      setOpen,
      isMobile,
      isOverlay,
      overlayOpen,
      setOverlayOpen,
      toggleSidebar,
      hovered,
      setHovered,
      canBeExpanded,
    ],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        data-slot="sidebar-wrapper"
        style={
          {
            '--sidebar-width': SIDEBAR_WIDTH,
            '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          'group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

/**
 * The collapsible side rail. Renders desktop and mobile (sheet) variants
 * automatically. Place {@link SidebarHeader}, {@link SidebarContent}, and
 * {@link SidebarFooter} as children.
 *
 * @example
 * <Sidebar>
 *   <SidebarHeader><SidebarLogoHeader /></SidebarHeader>
 *   <SidebarContent>…</SidebarContent>
 *   <SidebarFooter>…</SidebarFooter>
 * </Sidebar>
 */
function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'icon',
  className,
  children,
  dir,
  ...props
}: React.ComponentProps<'div'> & {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
}) {
  const {
    isMobile,
    isOverlay,
    state,
    overlayOpen,
    setOverlayOpen,
    setHovered,
    setCollapsible,
  } = useSidebar();

  React.useLayoutEffect(() => {
    setCollapsible(collapsible);
  }, [collapsible, setCollapsible]);

  if (collapsible === 'none') {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          'flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground',
          className,
        )}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        {...props}
      >
        {children}
      </div>
    );
  }

  const sheet = isOverlay ? (
    <Sheet open={overlayOpen} onOpenChange={setOverlayOpen}>
      <SheetContent
        dir={dir}
        data-sidebar="sidebar"
        data-slot="sidebar"
        data-mobile="true"
        className="w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground"
        style={
          {
            '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
          } as React.CSSProperties
        }
        side={side}
        showCloseButton={false}
      >
        <div className="flex size-full flex-col">{children}</div>
      </SheetContent>
    </Sheet>
  ) : null;

  if (isMobile) {
    return sheet;
  }

  return (
    <>
      {sheet}
      <div
        className="group peer hidden text-sidebar-foreground md:block"
        data-state={state}
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-variant={variant}
        data-side={side}
        data-slot="sidebar"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Reserves the column the fixed sidebar floats over on desktop. */}
        <div
          data-slot="sidebar-gap"
          className={cn(
            'relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
            'group-data-[collapsible=offcanvas]:w-0',
            'group-data-[side=right]:rotate-180',
            variant === 'floating' || variant === 'inset'
              ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
              : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
          )}
        />
        <div
          data-slot="sidebar-container"
          data-side={side}
          className={cn(
            'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear data-[side=left]:left-0 data-[side=left]:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)] data-[side=right]:right-0 data-[side=right]:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)] md:flex',
            variant === 'floating' || variant === 'inset'
              ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
              : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
            className,
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            data-slot="sidebar-inner"
            className="flex size-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-sm"
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Toggle for the overlay sidebar. Renders nothing while the sidebar is
 * pinned, where the hover-revealed {@link SidebarHeaderTrigger} inside
 * {@link SidebarLogoHeader} takes its place.
 */
function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { isOverlay, toggleSidebar } = useSidebar();

  if (!isOverlay) {
    return null;
  }

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      aria-label="Toggle Sidebar"
      variant="ghost"
      className={className}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <Icon name="Menu" size="auto" />
    </Button>
  );
}

/** Drag handle along the sidebar edge that toggles open/closed when clicked. */
function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        'absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border sm:flex',
        'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-sidebar',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      {...props}
    />
  );
}

/** Main content area positioned alongside the sidebar (gets inset chrome when `variant="inset"`). */
function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        'relative flex w-full flex-1 flex-col bg-background md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2',
        className,
      )}
      {...props}
    />
  );
}

/** Search input pre-styled to slot into a {@link SidebarHeader}. */
function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn('h-8 w-full bg-background shadow-none', className)}
      {...props}
    />
  );
}

/** Top region of a {@link Sidebar}; typically holds {@link SidebarLogoHeader} or a back button. */
function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
}

/** Bottom region of a {@link Sidebar}; typically holds the user menu or support links. */
function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
}

/** Horizontal divider tinted with the sidebar border token. */
function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn('mx-2 w-auto bg-sidebar-border', className)}
      {...props}
    />
  );
}

/** Scrollable middle region of a {@link Sidebar}; container for one or more {@link SidebarGroup}s. */
function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto',
        // TODO(chris)[ENG-4272]: temporary band-aid for collapsed-state overflow — see David's note on ENG-4227.
        //   When the proper UX lands, restore `group-data-[collapsible=icon]:overflow-hidden`
        //   (commented below) and drop the `group-data-[collapsible=icon]:no-scrollbar` workaround.
        'group-data-[collapsible=icon]:no-scrollbar',
        // 'group-data-[collapsible=icon]:overflow-hidden',
        className,
      )}
      {...props}
    />
  );
}

/** A logical grouping of related menu items inside {@link SidebarContent}. */
function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      {...props}
    />
  );
}

/** Optional caption above a {@link SidebarGroup}. Hidden when the sidebar is collapsed to icons. */
function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        'flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 ring-sidebar-ring outline-hidden transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

/** Right-aligned action button rendered next to a {@link SidebarGroupLabel} (e.g. "+ Add"). */
function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        'absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

/** Direct child of {@link SidebarGroup} that holds the menu list. */
function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn('w-full text-sm', className)}
      {...props}
    />
  );
}

/** Vertical list of menu items. Wraps each item in a {@link SidebarMenuItem}. */
function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn('flex w-full min-w-0 flex-col gap-1', className)}
      {...props}
    />
  );
}

/** Single row in a {@link SidebarMenu}; contains a {@link SidebarMenuButton} and optional action/badge. */
function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn('group/menu-item relative', className)}
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = tv({
  base: 'peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm ring-sidebar-ring outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:gap-3! group-data-[collapsible=icon]:p-3! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>span:last-child]:opacity-100 [&>span:last-child]:transition-opacity group-data-[collapsible=icon]:[&>span:last-child]:opacity-0 [&>svg]:size-4 [&>svg]:shrink-0',
  variants: {
    variant: {
      default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
      outline:
        'bg-background shadow-[0_0_0_1px_var(--sidebar-border)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_var(--sidebar-accent)]',
    },
    size: {
      default: 'h-8 text-sm',
      sm: 'h-7 text-xs',
      lg: 'h-12 text-sm',
    },
    hideIconWhenExpanded: {
      true: '[&>svg]:hidden group-data-[state=collapsed]:[&>svg]:block',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
    hideIconWhenExpanded: false,
  },
});

type SidebarMenuButtonVariants = Omit<
  VariantProps<typeof sidebarMenuButtonVariants>,
  'hideIconWhenExpanded'
> & {
  /**
   * Hides the row's icon while the sidebar is expanded and reveals it once
   * collapsed to icon-only. Useful for secondary nav clusters where the
   * label carries meaning when expanded, and a clickable icon is enough
   * once collapsed.
   */
  hideIconWhenExpanded?: boolean;
};

/**
 * Clickable row inside a {@link SidebarMenu}. Carries icon + label and an
 * optional `tooltip` shown when the sidebar is collapsed.
 *
 * Set `isActive` when the row represents the current page. Pair with `asChild`
 * to render as an arbitrary element such as `<NavLink>` or `<a>`. For
 * react-router-bound rows, prefer {@link SidebarMenuNavButton}; for external
 * URLs, prefer {@link SidebarMenuLinkButton}. Both handle `isActive` (where
 * applicable) and mobile-sheet auto-close for you.
 *
 * @example
 * <SidebarMenuButton tooltip="Settings" isActive={location === '/settings'}>
 *   <Icon name="Settings" size="auto" />
 *   <span>Settings</span>
 * </SidebarMenuButton>
 */
function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  hideIconWhenExpanded = false,
  tooltip,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & SidebarMenuButtonVariants) {
  const Comp = asChild ? Slot : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        sidebarMenuButtonVariants({
          variant,
          size,
          hideIconWhenExpanded,
        }),
        className,
      )}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  const tooltipProps =
    typeof tooltip === 'string' ? { children: tooltip } : tooltip;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        {...tooltipProps}
      />
    </Tooltip>
  );
}

/** Right-aligned action button inside a {@link SidebarMenuItem} (e.g. overflow menu). */
function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        'absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0',
        showOnHover &&
          'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground aria-expanded:opacity-100 md:opacity-0',
        className,
      )}
      {...props}
    />
  );
}

/** Numeric or text badge anchored to the right of a {@link SidebarMenuButton} (e.g. unread count). */
function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        'pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-sidebar-foreground tabular-nums select-none group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1',
        className,
      )}
      {...props}
    />
  );
}

/** Placeholder row used while menu data is loading. Pass `showIcon` to also reserve an icon slot. */
function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean;
}) {
  // Stable random width per mount so a list of skeletons doesn't look uniform.
  const [width] = React.useState(
    () => `${Math.floor(Math.random() * 40) + 50}%`,
  );

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

/** Indented sub-menu list rendered beneath a parent {@link SidebarMenuButton}. */
function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5 group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}

/** Single row inside a {@link SidebarMenuSub}. */
function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn('group/menu-sub-item relative', className)}
      {...props}
    />
  );
}

/** Clickable row inside a {@link SidebarMenuSub}. Defaults to rendering an `<a>`; pass `asChild` to swap. */
function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground ring-sidebar-ring outline-hidden group-data-[collapsible=icon]:hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[size=md]:text-sm data-[size=sm]:text-xs [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground',
        className,
      )}
      {...props}
    />
  );
}

// Eluve Compositions

/**
 * Hover-revealed toggle button in the sidebar header. Stays hidden while the
 * sidebar is expanded and the cursor is outside; fades in on hover or when the
 * sidebar is collapsed. See {@link SidebarLogoHeader} for usage example.
 */
function SidebarHeaderTrigger() {
  const { hovered, isMobile, open, toggleSidebar, state } = useSidebar();
  const isCollapsed = state !== 'expanded';

  if (isMobile) {
    return null;
  }

  const button = (
    <button
      data-slot="sidebar-header-trigger"
      data-sidebar="header-trigger"
      className={sidebarMenuButtonVariants({
        className: cn(
          'size-10! justify-center self-center opacity-0 transition-opacity duration-200 ease-linear',
          isCollapsed && 'self-start',
          (isCollapsed || hovered) && 'opacity-100',
        ),
      })}
      onClick={() => toggleSidebar()}
      aria-label="Toggle Sidebar"
    >
      <Icon name="Menu" size="auto" />
    </button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" align="center" hidden={open}>
        Toggle Sidebar
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Branded header for the primary app sidebar. Renders the Eluve wordmark with
 * a collapse-state animation and slots in a {@link SidebarHeaderTrigger}
 * beside it. Pass `onClick` to make the logo navigate (typically to home).
 *
 * @example
 * <SidebarHeader>
 *   <SidebarLogoHeader onClick={() => navigate('/home')} />
 * </SidebarHeader>
 */
function SidebarLogoHeader({ onClick }: { onClick?: () => void }) {
  const { canBeExpanded, isMobile, state } = useSidebar();
  const hasOnClick = Boolean(onClick);
  const isExpanded = state === 'expanded';
  const isCollapsed = !isExpanded && !isMobile;

  return (
    <div
      className={cn(
        'flex h-14 items-start justify-between gap-2 transition-[height] duration-200 ease-linear',
        isCollapsed && 'flex-col pb-0',
        isCollapsed && canBeExpanded && 'h-28',
      )}
    >
      <div
        className={cn(
          'my-2 flex h-10 shrink-0 items-center justify-center rounded-lg px-2 transition-[background,padding] duration-200 ease-linear',
          hasOnClick && 'cursor-pointer',
          isCollapsed && 'bg-sidebar-primary px-3',
        )}
        onClick={onClick}
      >
        <div
          className={cn(
            '-mt-0.5 w-22 overflow-hidden transition-[width] duration-200 ease-linear',
            isCollapsed && 'w-4',
          )}
        >
          <EluveNameLogo
            className={cn(
              'h-10.5 transition-[fill] duration-200 ease-linear',
              isCollapsed && 'fill-sidebar-primary-foreground',
            )}
          />
        </div>
      </div>
      {canBeExpanded && <SidebarHeaderTrigger />}
    </div>
  );
}

/** Spacer that pushes following {@link SidebarGroup}s to the bottom of {@link SidebarContent}. */
function SidebarContentSpacer() {
  return <div className="flex-1" />;
}

/**
 * Sidebar nav row bound to a react-router route. Renders a {@link NavLink}
 * inside a {@link SidebarMenuButton}, derives `isActive` from {@link useMatch}
 * (override with the `isActive` prop), and auto-closes the mobile sheet on click.
 *
 * @example
 * <SidebarMenuNavButton to="/appointments" tooltip="Appointments">
 *   <Icon name="FileEdit" size="auto" />
 *   <span>Appointments</span>
 * </SidebarMenuNavButton>
 */
function SidebarMenuNavButton({
  to,
  tooltip,
  isActive: forcedActive,
  size,
  variant,
  hideIconWhenExpanded,
  onClick,
  children,
  ...navLinkProps
}: Omit<React.ComponentProps<typeof NavLink>, 'to'> & {
  to: string;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  isActive?: boolean;
  size?: SidebarMenuButtonVariants['size'];
  variant?: SidebarMenuButtonVariants['variant'];
  hideIconWhenExpanded?: SidebarMenuButtonVariants['hideIconWhenExpanded'];
}) {
  const { overlayOpen, setOverlayOpen } = useSidebar();
  const match = useMatch(to);

  return (
    <SidebarMenuButton
      asChild
      isActive={forcedActive ?? Boolean(match)}
      tooltip={tooltip}
      size={size}
      variant={variant}
      hideIconWhenExpanded={hideIconWhenExpanded}
      onClick={(event) => {
        if (overlayOpen) {
          setOverlayOpen(false);
        }
        // Slot rewrites onClick onto the child <a>, so at runtime the event
        // is an anchor event despite the prop's button typing.
        onClick?.(event as unknown as React.MouseEvent<HTMLAnchorElement>);
      }}
    >
      <NavLink to={to} {...navLinkProps}>
        {children}
      </NavLink>
    </SidebarMenuButton>
  );
}

/**
 * Sidebar nav row bound to an external URL. Renders an `<a>` inside a
 * {@link SidebarMenuButton} (defaulting to `target="_blank"` with safe `rel`),
 * and auto-closes the mobile sheet on click. Use {@link SidebarMenuNavButton}
 * for in-app routes instead.
 *
 * @example
 * <SidebarMenuLinkButton href="https://eluve.com/help" tooltip="Help">
 *   <Icon name="CircleHelp" size="auto" />
 *   <span>Help</span>
 * </SidebarMenuLinkButton>
 */
function SidebarMenuLinkButton({
  href,
  rel = 'noreferrer',
  target = '_blank',
  tooltip,
  size,
  variant,
  hideIconWhenExpanded,
  onClick,
  children,
  ...anchorProps
}: Omit<React.ComponentProps<'a'>, 'href'> & {
  href: string;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  size?: SidebarMenuButtonVariants['size'];
  variant?: SidebarMenuButtonVariants['variant'];
  hideIconWhenExpanded?: SidebarMenuButtonVariants['hideIconWhenExpanded'];
}) {
  const { overlayOpen, setOverlayOpen } = useSidebar();

  return (
    <SidebarMenuButton
      asChild
      tooltip={tooltip}
      size={size}
      variant={variant}
      hideIconWhenExpanded={hideIconWhenExpanded}
      onClick={(event) => {
        if (overlayOpen) {
          setOverlayOpen(false);
        }
        // Slot rewrites onClick onto the child <a>, so at runtime the event
        // is an anchor event despite the prop's button typing.
        onClick?.(event as unknown as React.MouseEvent<HTMLAnchorElement>);
      }}
    >
      <a href={href} rel={rel} target={target} {...anchorProps}>
        {children}
      </a>
    </SidebarMenuButton>
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarContentSpacer,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarHeaderTrigger,
  SidebarInput,
  SidebarInset,
  SidebarLogoHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuLinkButton,
  SidebarMenuNavButton,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
